import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
  Backdrop,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import { Visibility, VisibilityOff, AccountCircle } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import _ from 'lodash';
import PropTypes from 'prop-types';
import * as actions from '../../redux/actions';
import api from '../../utils/functions/api';
import SearchField from '../../components/SearchField';
import { EmployeeForm } from '../../components/Forms';
import Loading from '../../components/Loading';
import { Error } from '../../components/Error';
import { DevWinBadge } from '../../components/DevPageBadge';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90vw',
  boxShadow: 24,
  p: 4,
};

const defaultValue = {
  firstname: '',
  lastname: '',
  department: '',
  role: '',
  username: '',
  password: '',
  phone_number: '',
};

function Employees({ title, subtitle }) {
  const dispatch = useDispatch();
  const employee = useSelector((state) => state.employee);
  const roletype = useSelector((state) => state.roletype);
  const department = useSelector((state) => state.department);
  const history = useHistory();
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [name, setName] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(100);
  const [total, setTotal] = useState(undefined);
  const [open, setOpen] = useState(false);
  const [addUser, setAddUser] = useState(false);
  const [addedEmployeeImage, setAddedEmployeeImage] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editEmployeeId, setEditEmployeeId] = useState(null);
  const [editEmployeeData, setEditEmployeeData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editImage, setEditImage] = useState([]);
  const [selectDepartment, setSelectDepartment] = useState('');
  const [permFilter, setPermFilter] = useState(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);
  const [accountForm, setAccountForm] = useState({ username: '', password: '', confirmPassword: '' });
  const [showAccountPassword, setShowAccountPassword] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    dispatch(actions.employeeAll({ name, page, size, selectDepartment, permission: permFilter || '' }));
    dispatch(actions.roletypeAll(''));
    dispatch(actions.departmentAll({}));
    return () => {};
  }, [name, page, size, selectDepartment, permFilter]);

  useEffect(() => {
    setTotal(employee?.total);
    return () => {};
  }, [employee]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setName(searchTerm);
      setPage(1);
    }, 700);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  console.log('Errors', errors);
  const onSubmit = async (data) => {
    try {
      console.log(data);
      // eslint-disable-next-line no-param-reassign
      const payload = {
        ...data,
        salary: {
          month: data?.salary?.month,
          day: data?.salary?.month / 30,
          hr: data?.salary?.month / 240,
        },
      };
      if (addUser === true) {
        if (data.password === data.confirmPassword) {
          await dispatch(actions.userRegister(payload));
          reset(defaultValue);
          alert('สำเร็จ');
          handleClose();
          setAddUser(false);
          await dispatch(actions.employeeAll({ name, page, size, selectDepartment, permission: permFilter || '' }));
        } else {
          alert('กรุณาใส่รหัสผ่านให้ตรงกัน');
        }
      } else {
        await dispatch(actions.employeeCreate(payload));
        reset(defaultValue);
        alert('สำเร็จ');
        handleClose();
        setAddUser(false);
        setAddedEmployeeImage([]);
        await dispatch(actions.employeeAll({ name, page, size, selectDepartment, permission: permFilter || '' }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteEmployee = async (id) => {
    const confirm = window.confirm('ยืนยันการลบข้อมูล');
    if (confirm) {
      try {
        await dispatch(actions.employeeDelete(id));
        await dispatch(actions.employeeAll({ name, page, size, selectDepartment, permission: permFilter || '' }));
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handlePushToEditEmployee = async (id) => {
    history.push(`/management/employee/edit/${id}`);
  };

  const handleOpenEdit = async (id) => {
    setEditOpen(true);
    setEditEmployeeId(id);
    setEditLoading(true);
    setEditEmployeeData(null);
    try {
      const { data } = await api.get(`${process.env.REACT_APP_API_URL}/employee/${id}`);
      setEditEmployeeData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setEditLoading(false);
    }
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditEmployeeId(null);
    setEditEmployeeData(null);
    setEditImage([]);
    reset({});
  };

  const onEditSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        salary: {
          month: data?.salary?.month,
          day: data?.salary?.month / 30,
          hr: data?.salary?.month / 240,
        },
      };
      if (!_.isEmpty(editImage)) {
        payload.image = { image: editImage[0]?.data_url, imageType: 'profile', alt: '' };
      }
      await dispatch(actions.employeePut(editEmployeeId, payload));
      alert('บันทึกสำเร็จ');
      handleCloseEdit();
      dispatch(actions.employeeAll({ name, page, size, selectDepartment, permission: permFilter || '' }));
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenAccount = async () => {
    setAccountForm({ username: '', password: '', confirmPassword: '' });
    setAccountInfo(null);
    setShowAccountPassword(false);
    if (editEmployeeData?.uid) {
      try {
        const info = await dispatch(actions.userGetByEmployeeId(editEmployeeId));
        setAccountInfo(info);
      } catch {
        setAccountInfo(null);
      }
    }
    setAccountOpen(true);
  };

  const handleSaveAccount = async () => {
    if (accountForm.password !== accountForm.confirmPassword) {
      alert('รหัสผ่านไม่ตรงกัน');
      return;
    }
    if (!accountForm.password) {
      alert('กรุณาระบุรหัสผ่าน');
      return;
    }
    try {
      if (editEmployeeData?.uid) {
        await dispatch(actions.userPut(editEmployeeData.uid, { password: accountForm.password }));
        alert('เปลี่ยนรหัสผ่านสำเร็จ');
      } else {
        if (!accountForm.username) { alert('กรุณาระบุ Username'); return; }
        await dispatch(actions.userLinkToEmployee(editEmployeeId, {
          username: accountForm.username,
          password: accountForm.password,
        }));
        const { data } = await api.get(`${process.env.REACT_APP_API_URL}/employee/${editEmployeeId}`);
        setEditEmployeeData(data);
        alert('สร้างบัญชีผู้ใช้สำเร็จ');
      }
      setAccountOpen(false);
    } catch (error) {
      alert(error?.response?.data?.error?.message || 'เกิดข้อผิดพลาด');
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setSize(event.target.value);
    setPage(1);
  };

  const handleSelectDepartment = (type) => {
    setSelectDepartment(type);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const renderAddButtom = () => (
    <div className="flex flex-row justify-end pb-4">
      <div>
        <Button variant="contained" onClick={handleOpen}>
          เพิ่ม
        </Button>
      </div>
    </div>
  );

  const PERM_FILTERS = [
    { label: 'ทั้งหมด', value: null },
    { label: 'Admin', value: 'admin', color: 'error' },
    { label: 'HR', value: 'hr_management', color: 'success' },
    { label: 'Finance', value: 'finance_approve', color: 'secondary' },
    { label: 'ทั่วไป', value: 'none', color: 'default' },
  ];

  const renderSearch = () => (
    <Card>
      <div className="p-4 flex flex-col gap-3">
        <div className="w-full md:w-1/2">
          <SearchField
            label="ค้นหา"
            fullWidth
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex flex-row flex-wrap gap-2">
          {PERM_FILTERS.map((f) => (
            <Chip
              key={String(f.value)}
              label={f.label}
              color={permFilter === f.value ? (f.color || 'primary') : 'default'}
              variant={permFilter === f.value ? 'filled' : 'outlined'}
              onClick={() => { setPermFilter(f.value); setPage(1); }}
              clickable
            />
          ))}
        </div>
      </div>
    </Card>
  );

  const renderModal = () => (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <Card sx={style} className="max-w-4xl">
          <div className="py-2">เพิ่มข้อมูลพนักงาน</div>
          <div className="py-2">
            <form onSubmit={handleSubmit(onSubmit)}>
              <EmployeeForm
                control={control}
                Controller={Controller}
                errors={errors}
                employee={employee}
                roletype={roletype}
                addUser={addUser}
                setAddUser={setAddUser}
                employeeImage={addedEmployeeImage}
                setEmployeeImage={setAddedEmployeeImage}
                department={department}
                watch={watch}
                setValue={setValue}
              />
              <div className="flex flex-row justify-end gap-1 py-4">
                <Button variant="contained" type="submit">
                  บันทึก
                </Button>
                <Button variant="outlined" onClick={handleClose}>
                  ยกเลิก
                </Button>
              </div>
            </form>
          </div>
        </Card>
      </Fade>
    </Modal>
  );

  const renderAccountDialog = () => (
    <Dialog open={accountOpen} onClose={() => setAccountOpen(false)} maxWidth="xs" fullWidth>
      {editEmployeeData?.uid ? (
        <>
          <DialogTitle fontWeight={700}>บัญชีผู้ใช้</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Username"
                fullWidth
                size="small"
                value={accountInfo?.username || '(กำลังโหลด...)'}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiInputBase-root': { bgcolor: '#f5f5f5' } }}
              />
              <TextField
                label="รหัสผ่านใหม่"
                type={showAccountPassword ? 'text' : 'password'}
                fullWidth
                size="small"
                value={accountForm.password}
                onChange={(e) => setAccountForm((p) => ({ ...p, password: e.target.value }))}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowAccountPassword((v) => !v)}>
                        {showAccountPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="ยืนยันรหัสผ่านใหม่"
                type={showAccountPassword ? 'text' : 'password'}
                fullWidth
                size="small"
                value={accountForm.confirmPassword}
                onChange={(e) => setAccountForm((p) => ({ ...p, confirmPassword: e.target.value }))}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setAccountOpen(false)} variant="outlined">ปิด</Button>
            <Button onClick={handleSaveAccount} variant="contained">เปลี่ยนรหัสผ่าน</Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle fontWeight={700}>สร้างบัญชีผู้ใช้</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Username"
                fullWidth
                size="small"
                value={accountForm.username}
                onChange={(e) => setAccountForm((p) => ({ ...p, username: e.target.value }))}
                autoComplete="off"
              />
              <TextField
                label="Password"
                type={showAccountPassword ? 'text' : 'password'}
                fullWidth
                size="small"
                value={accountForm.password}
                onChange={(e) => setAccountForm((p) => ({ ...p, password: e.target.value }))}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowAccountPassword((v) => !v)}>
                        {showAccountPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="ยืนยัน Password"
                type={showAccountPassword ? 'text' : 'password'}
                fullWidth
                size="small"
                value={accountForm.confirmPassword}
                onChange={(e) => setAccountForm((p) => ({ ...p, confirmPassword: e.target.value }))}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setAccountOpen(false)} variant="outlined">ยกเลิก</Button>
            <Button onClick={handleSaveAccount} variant="contained">สร้างบัญชี</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  const renderEditDialog = () => (
    <Dialog open={editOpen} onClose={handleCloseEdit} maxWidth="xl" fullWidth PaperProps={{ sx: { maxHeight: '92vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            แก้ไขข้อมูลพนักงาน
            {editEmployeeData?.employee_id && (
              <Chip label={editEmployeeData.employee_id} size="small" variant="outlined" sx={{ ml: 1.5, fontWeight: 400 }} />
            )}
          </Typography>
          <IconButton size="small" onClick={handleCloseEdit}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {editLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : editEmployeeData ? (
          <form id="edit-emp-form" onSubmit={handleSubmit(onEditSubmit)}>
            <EmployeeForm
              control={control}
              Controller={Controller}
              errors={errors}
              employee={editEmployeeData}
              roletype={roletype}
              addUser={false}
              setAddUser={() => {}}
              employeeImage={editImage}
              setEmployeeImage={setEditImage}
              department={department}
              watch={watch}
              setValue={setValue}
            />
          </form>
        ) : null}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            color={editEmployeeData?.uid ? 'secondary' : 'inherit'}
            startIcon={<AccountCircle fontSize="small" />}
            onClick={handleOpenAccount}
            size="small"
          >
            {editEmployeeData?.uid ? 'จัดการบัญชีผู้ใช้' : 'สร้าง Username'}
          </Button>
          <Chip
            label={editEmployeeData?.uid ? 'มีบัญชีแล้ว' : 'ยังไม่มีบัญชี'}
            color={editEmployeeData?.uid ? 'success' : 'default'}
            size="small"
          />
        </Box>
        <Button variant="outlined" onClick={handleCloseEdit}>ยกเลิก</Button>
        <Button variant="contained" type="submit" form="edit-emp-form">บันทึก</Button>
      </DialogActions>
    </Dialog>
  );

  const renderTable = () => (
    <div className="my-2">
      <Paper>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <div className="font-bold">No.</div>
                </TableCell>
                <TableCell>
                  <div className="font-bold"> Name</div>
                </TableCell>
                <TableCell>
                  <div className="font-bold"> Phone</div>
                </TableCell>

                <TableCell>
                  <div className="font-bold"> Role</div>
                </TableCell>
                <TableCell>
                  <div className="font-bold">สิทธิ์</div>
                </TableCell>
                <TableCell>
                  <div className="font-bold"> </div>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!_.isEmpty(employee.rows) ? (
                employee.rows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    onDoubleClick={() => handleOpenEdit(row.id)}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <TableCell component="th" scope="row">
                      {(page - 1) * size + index + 1}
                    </TableCell>
                    <TableCell>{`${row.firstname} ${row.lastname}`}</TableCell>
                    <TableCell>{`${row.phone_number || '-'}`}</TableCell>
                    <TableCell>{`${row?.role?.name}`}</TableCell>
                    <TableCell>
                      <div className="flex flex-row flex-wrap gap-1">
                        {row?.permissions?.admin && (
                          <Chip label="Admin" color="error" size="small" />
                        )}
                        {row?.permissions?.hr_management && (
                          <Chip label="HR" color="success" size="small" />
                        )}
                        {row?.permissions?.finance_approve && (
                          <Chip label="Finance" color="secondary" size="small" />
                        )}
                        {!row?.permissions?.admin && !row?.permissions?.hr_management && !row?.permissions?.finance_approve && (
                          <Chip label="ทั่วไป" variant="outlined" size="small" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        sx={{ px: 1, py: 0, fontSize: '0.7rem', minWidth: 0 }}
                        onClick={(e) => { e.stopPropagation(); handleDeleteEmployee(row?.id); }}
                      >
                        ลบ
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <div className="text-center">ไม่มีข้อมูล</div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[]}
          onRowsPerPageChange={handleChangeRowsPerPage}
          page={page - 1}
          count={total || 1}
          rowsPerPage={size}
          onPageChange={handleChangePage}
        />
      </Paper>
    </div>
  );

  if (employee.isLoading) {
    return <Loading />;
  }
  if (!employee.isLoading && employee.isCompleted) {
    return (
      <div>
        <DevWinBadge num={135} name="Mgmt · Add Employee Modal" open={open} />
        {renderModal()}
        <DevWinBadge num={136} name="Mgmt · Edit Employee Dialog" open={editOpen} />
        {renderEditDialog()}
        <DevWinBadge num={137} name="Mgmt · Add Account Dialog" open={accountOpen} />
        {renderAccountDialog()}
        <div className="flex justify-between">

          <div className="mt-6">{renderAddButtom()}</div>
        </div>
        {renderSearch()}
        {renderTable()}
      </div>
    );
  }
  return <Error />;
}

Employees.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

Employees.defaultProps = {
  title: '',
  subtitle: '',
};

export default Employees;
